/**
 * useIdleLogout — PC 유휴 4시간 자동 로그아웃 + 5분 전 경고(12 설계 §7-2, 04 세션 정책 §3).
 *
 *  - 활동(mousemove keydown wheel touchstart scroll)마다 마지막 활동 시각을 localStorage 에 남긴다(15초 간격).
 *    탭끼리 같은 값을 보므로 다른 탭에서 일하고 있으면 이 탭도 로그아웃되지 않는다.
 *    이 값은 MO 에서도 남긴다 — 60일 미사용 만료 판정에 쓴다(§7-4).
 *  - PC(kind:"pc")만: 남은 시간이 5분 이하면 경고(warning=true), 0초면 로그아웃 + 접속 기록 idle_logout
 *    + 로그인 화면 안내 "오래 사용하지 않아 로그아웃됐습니다".
 *  - 경고가 떠 있는 동안 이 탭의 마우스 움직임은 활동으로 치지 않는다 — [계속 사용]으로만 연장(의도 확인).
 *  라이브러리(react-idle-timer) 없이 자체 구현(§7-2).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { IDLE_LOGOUT_MS, IDLE_WARNING_MS } from "@/domain/session";
import { detectDeviceKind } from "@/domain/device_info";
import { readLastActivity, tryIdleLogoutLock, writeLastActivity } from "@/lib/sessionLocal";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "wheel", "touchstart", "scroll"] as const;
const WRITE_THROTTLE_MS = 15_000;
const TICK_MS = 1_000;

export interface IdleLogoutState {
  /** 경고 모달을 띄울 때 */
  warning: boolean;
  /** 남은 시간(ms) — 경고 중에만 의미 있음 */
  remainingMs: number;
  /** [계속 사용] */
  keepUsing: () => void;
  /** [지금 로그아웃] */
  logoutNow: () => void;
}

function isPcDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return detectDeviceKind({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints }) === "pc";
}

export function useIdleLogout(): IdleLogoutState {
  const { user, logout } = useAuth();
  const active = !!user;
  const [isPc] = useState(isPcDevice);
  const [warning, setWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(IDLE_WARNING_MS);
  const warningRef = useRef(false);
  const lastWriteRef = useRef(0);
  const doneRef = useRef(false);

  // 활동 기록 — 모든 기기
  useEffect(() => {
    if (!active) return;
    doneRef.current = false;
    const now = Date.now();
    writeLastActivity(now);
    lastWriteRef.current = now;
    const onActivity = () => {
      if (warningRef.current) return;
      const t = Date.now();
      if (t - lastWriteRef.current < WRITE_THROTTLE_MS) return;
      lastWriteRef.current = t;
      writeLastActivity(t);
    };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true, capture: true });
    return () => {
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity, { capture: true });
    };
  }, [active]);

  // 유휴 판정 — PC 만
  useEffect(() => {
    if (!active || !isPc) return;
    const tick = () => {
      if (doneRef.current) return;
      const now = Date.now();
      const last = readLastActivity() ?? now;
      const remaining = IDLE_LOGOUT_MS - (now - last);
      if (remaining <= 0) {
        doneRef.current = true;
        warningRef.current = false;
        setWarning(false);
        // 다른 탭이 이미 처리 중이면 그 탭의 signOut 이 이 탭에도 전파된다
        if (tryIdleLogoutLock(now)) void logout("idle_logout");
        return;
      }
      if (remaining <= IDLE_WARNING_MS) {
        warningRef.current = true;
        setWarning(true);
        setRemainingMs(remaining);
      } else if (warningRef.current) {
        warningRef.current = false;
        setWarning(false);
      }
    };
    tick();
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [active, isPc, logout]);

  const keepUsing = useCallback(() => {
    const t = Date.now();
    lastWriteRef.current = t;
    writeLastActivity(t);
    warningRef.current = false;
    setWarning(false);
  }, []);

  const logoutNow = useCallback(() => {
    doneRef.current = true;
    warningRef.current = false;
    setWarning(false);
    void logout("logout");
  }, [logout]);

  return { warning: active && isPc && warning, remainingMs, keepUsing, logoutNow };
}
